import { type ReactNode, useMemo } from "react";

import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { CurrencyInput, PercentInput, Select } from "@components/v2";
import { Currencies, formatting } from "@north-beam/nb-common";

import { type CogValueSelection } from "../../types";

interface Props {
  title: string;
  tooltipText?: ReactNode;
  aov: number;
  selected: CogValueSelection;
  value: number;
  disabled?: boolean;
  onUpdateSelected: (selected: CogValueSelection) => void;
  onUpdateValue: (value: number) => void;
}

export const FormRow = ({
  title,
  tooltipText,
  aov,
  selected,
  value,
  disabled,
  onUpdateSelected,
  onUpdateValue,
}: Props) => {
  const { user } = useUser();

  const currencySymbol = useMemo(() => {
    const code = user.currencies.currentCurrency;
    return Currencies.getAttribute(code, "symbol") ?? code;
  }, [user.currencies.currentCurrency]);

  const valueToPercent = (value: number) => (value / aov) * 100;

  const formatCurrency = (value: number) => {
    return formatting.formatCurrency.exact(value, {
      code: user.currencies.currentCurrency,
    });
  };

  return (
    <div className="flex items-center gap-5">
      <div className="flex items-center gap-2 w-[240px]">
        <Select
          buttonClassName="w-[65px]"
          disabled={disabled}
          value={selected}
          options={[
            { label: "%", value: "percent" },
            { label: currencySymbol, value: "value" },
          ]}
          onChange={(selected) => {
            if (selected) {
              onUpdateSelected(selected);
            }
          }}
        />
        {selected === "percent" ? (
          <PercentInput
            className="w-full"
            disabled={disabled}
            defaultValue={0}
            value={valueToPercent(value)}
            onChange={(value) => {
              if (value !== undefined) {
                onUpdateValue((aov * value) / 100);
              }
            }}
          />
        ) : (
          <CurrencyInput
            className="w-full"
            disabled={disabled}
            defaultValue={0}
            value={value}
            onChange={(value) => {
              if (value !== undefined) {
                onUpdateValue(value);
              }
            }}
          />
        )}
      </div>
      <div className="flex items-center">
        <div className="text-v2-secondary w-[70px]">
          {formatCurrency(value)}
        </div>
        <div className="text-sm font-semibold">
          {tooltipText ? (
            <GenericTooltip placement="top" content={tooltipText}>
              {title}
            </GenericTooltip>
          ) : (
            <>{title}</>
          )}
        </div>
      </div>
    </div>
  );
};
